import {
  ArrowRight,
  Building2,
  Check,
  Gift,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";

import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";

const plans = [
  {
    id: "free",
    name: "Free",
    audience: "Early Access",
    description:
      "Explore Cullath with essential features for individual users and small teams.",
    price: "FREE",
    priceDescription: "Early Access",
    Icon: Gift,
    buttonText: "Get Started Free",
    buttonHref: "https://connect.cullath.com/",
    footerText: "Perfect for getting started",
    features: [
      "Connect – Team Messaging",
      "Assist – Remote Support",
      "Desk – Ticket Management",
      "AI Assistant (Basic)",
      "File Sharing",
      "Up to 10 Users",
      "Up to 5 GB Storage",
    ],
  },
  {
    id: "business",
    name: "Business",
    audience: "For Growing Teams",
    description:
      "Advanced features and controls to help teams collaborate and get more done.",
    price: "Contact Sales",
    priceDescription: "Tailored for your team",
    Icon: TrendingUp,
    buttonText: "Contact Sales",
    buttonHref: "/contact-us",
    footerText: "We'll build the right plan for you",
    featured: true,
    features: [
      "Everything in Free",
      "Advanced Administration",
      "Enhanced Security & Permissions",
      "Priority Email Support",
      "Audit Logs & Reports",
      "Increased Storage",
      "And more upcoming features",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    audience: "For Large Organizations",
    description:
      "Custom solutions with dedicated support and enterprise-grade capabilities.",
    price: "Custom Pricing",
    priceDescription: "Built for your organization",
    Icon: Building2,
    buttonText: "Contact Us",
    buttonHref: "/contact-us",
    footerText: "Let's discuss your requirements",
    features: [
      "Everything in Business",
      "Custom Deployment Options",
      "Dedicated Account Manager",
      "SLA & Uptime Guarantee",
      "Advanced Security & Compliance",
      "Unlimited Storage Options",
      "And more",
    ],
  },
];

export default function PricingPage() {

  
  const breadcrumbSchema = {
 "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": "https://cullath.com/pricing/#breadcrumb",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://cullath.com/",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Pricing",
      item: "https://cullath.com/pricing",
    },
  ],
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": "https://cullath.com/pricing/#webpage",
  url: "https://cullath.com/pricing",
  name: "Cullath Pricing | Simple Plans for Every Team",
  description:
    "Explore Cullath pricing plans for Connect, Desk, and Assist. Compare Free, Business, and Enterprise plans to find the right fit for your team.",
  isPartOf: {
    "@id": "https://cullath.com/#website",
  },
  about: {
    "@id": "https://cullath.com/#organization",
  },
  inLanguage: "en-US",
};

const pricingSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": "https://cullath.com/pricing/#breadcrumb",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://cullath.com/",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Pricing",
      item: "https://cullath.com/pricing",
    },
  ],
};

  return (

    <div className="pricing-page-shell">
       <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(webPageSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(pricingSchema),
        }}
      />
      <SiteHeader isProductPage isHeroPage />

      <main className="pricing-page">
        <section className="pricing-intro">
          <div className="pricing-intro__content">
            <h1>
              Cullath Pricing Plans {" "}
              <span>for Every Team.</span>
            </h1>

            <p>
             Start with our free plan and access <a href="https://cullath.com/connect"
              className="highlight-link">
           Cullath Connect
            </a> , <a href="https://cullath.com/assist"
              className="highlight-link">
           Assist 
            </a>, and <a href="https://cullath.com/desk"
              className="highlight-link">
           Desk 
            </a> . Upgrade as your team grows or contact us for enterprise pricing.
            </p>

            <div className="pricing-benefits" aria-label="Pricing benefits">
              <div className="pricing-benefit">
                <span className="pricing-benefit__icon">
                  <Check size={14} strokeWidth={3} />
                </span>
                <span>Easy to get started</span>
              </div>

              <span className="pricing-benefits__divider" />

              <div className="pricing-benefit">
                <span className="pricing-benefit__icon">
                  <Check size={14} strokeWidth={3} />
                </span>
                <span>Built for modern teams</span>
              </div>

              <span className="pricing-benefits__divider" />

              <div className="pricing-benefit">
                <span className="pricing-benefit__icon">
                  <Check size={14} strokeWidth={3} />
                </span>
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
        </section>

        <section
          className="pricing-plans-section"
          aria-label="Cullath pricing plans"
        >
          <div className="pricing-grid">
            {plans.map((plan) => {
              const Icon = plan.Icon;

              return (
                <article
                  key={plan.id}
                  className={`pricing-card pricing-card--${plan.id} ${
                    plan.featured ? "pricing-card--featured" : ""
                  }`}
                >
                  {plan.featured && (
                    <div className="pricing-popular">MOST POPULAR</div>
                  )}

                  <div className="pricing-card__header">
                    <div className="pricing-card__icon">
                      <Icon size={35} strokeWidth={1.9} />
                    </div>

                    <div className="pricing-card__title">
                      <h2>{plan.name}</h2>
                      <strong>{plan.audience}</strong>
                      <p>{plan.description}</p>
                    </div>
                  </div>

                  <div className="pricing-card__divider" />

                  <div className="pricing-card__price">
                    <h3>{plan.price}</h3>
                    <p>{plan.priceDescription}</p>
                  </div>

                  <ul className="pricing-card__features">
                    {plan.features.map((feature) => (
                      <li key={feature}>
                        <span className="pricing-feature-check">
                          <Check size={12} strokeWidth={3.2} />
                        </span>

                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="pricing-card__action">
                    <a
                      href={plan.buttonHref}
                      target={
                        plan.buttonHref.startsWith("http")
                          ? "_blank"
                          : undefined
                      }
                      rel={
                        plan.buttonHref.startsWith("http")
                          ? "noreferrer"
                          : undefined
                      }
                    >
                      {plan.buttonText}
                    </a>

                    <p>{plan.footerText}</p>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="pricing-security-bar">
            <div className="pricing-security-bar__item">
              <span className="pricing-security-bar__icon">
                <ShieldCheck size={30} strokeWidth={1.9} />
              </span>

              <p>
                Your data is secure with enterprise-grade security, privacy,
                <br className="pricing-desktop-break" />
                and compliance built into the Cullath platform.
              </p>
            </div>

            <span className="pricing-security-bar__divider" />

            <div className="pricing-security-bar__contact">
              <p>Have questions or need help choosing a plan?</p>

              <a href="/contact-us">
                Contact our team
                <ArrowRight size={19} />
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}